import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS } from "./adminStyles";

const API_URL = getBaseURL();

const UpdateScoreInner = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("adminToken");
  const toast = useToast();

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scoreData, setScoreData] = useState({
    team1_runs: 0, team1_wickets: 0, team1_overs: "0",
    team2_runs: 0, team2_wickets: 0, team2_overs: "0",
    status: "live"
  });

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    fetchMatchDetails();
  }, [matchId]);

  const fetchMatchDetails = async () => {
    try {
      const res = await axios.get(`${API_URL}/matches/${matchId}`);
      const data = Array.isArray(res.data) ? res.data[0] : res.data;
      setMatch(data);
      setScoreData({
        team1_runs: data.team1_runs || 0,
        team1_wickets: data.team1_wickets || 0,
        team1_overs: data.team1_overs || "0",
        team2_runs: data.team2_runs || 0,
        team2_wickets: data.team2_wickets || 0,
        team2_overs: data.team2_overs || "0",
        status: data.status || "live"
      });
    } catch (err) {
      toast.error("Failed to fetch match details.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/matches/${matchId}/score`, scoreData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("✅ Score updated successfully!");
      fetchMatchDetails();
    } catch (err) {
      toast.error("Failed to update score.");
    } finally {
      setSaving(false);
    }
  };

  const ScoreInput = ({ label, value, onChange, type = "number", placeholder }) => (
    <div>
      <label style={S.label}>{label}</label>
      <input
        className="admin-input-focus"
        type={type}
        style={{ ...S.input, fontSize: "20px", fontWeight: 700, textAlign: "center" }}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </div>
  );

  if (loading) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <div style={{ color: COLORS.textMuted, fontSize: "14px" }}>Loading match...</div>
    </div>
  );

  if (!match) return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <div style={S.empty}>Match not found.</div>
    </div>
  );

  const isLive = scoreData.status === "live";

  return (
    <div style={{ ...S.page, maxWidth: "720px" }}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={{ textAlign: "center", marginBottom: "28px" }}>
        <div style={S.pageTitle}>🔴 Live Score Update</div>
        <div style={{
          background: COLORS.card,
          border: `1px solid ${isLive ? COLORS.green + "40" : COLORS.cardBorder}`,
          borderRadius: "14px",
          padding: "16px 24px",
          marginTop: "16px",
          display: "inline-flex",
          alignItems: "center",
          gap: "16px",
        }}>
          <span style={{ fontSize: "24px", fontWeight: 800, color: COLORS.text }}>{match.team1}</span>
          <span style={{ fontSize: "13px", color: COLORS.textMuted, fontStyle: "italic" }}>vs</span>
          <span style={{ fontSize: "24px", fontWeight: 800, color: COLORS.text }}>{match.team2}</span>
          {isLive && (
            <span style={{
              background: COLORS.green + "15", border: `1px solid ${COLORS.green}30`, color: COLORS.green,
              fontSize: "10px", fontWeight: 700, padding: "4px 10px", borderRadius: "6px", textTransform: "uppercase",
              display: "flex", alignItems: "center", gap: "5px",
            }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: COLORS.green, animation: "pulse 1.5s infinite" }} />
              LIVE
            </span>
          )}
        </div>
      </div>

      <form onSubmit={handleUpdate}>
        {/* TEAM 1 SCORE */}
        <div style={{ ...S.card, borderTop: `3px solid ${COLORS.roleBat}` }}>
          <div style={{ fontSize: "14px", fontWeight: 700, color: COLORS.roleBat, marginBottom: "16px" }}>
            {match.team1} Innings
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px" }}>
            <ScoreInput label="Runs" value={scoreData.team1_runs} onChange={e => setScoreData({ ...scoreData, team1_runs: e.target.value })} />
            <ScoreInput label="Wickets" value={scoreData.team1_wickets} onChange={e => setScoreData({ ...scoreData, team1_wickets: e.target.value })} />
            <ScoreInput label="Overs" type="text" placeholder="e.g. 18.4" value={scoreData.team1_overs} onChange={e => setScoreData({ ...scoreData, team1_overs: e.target.value })} />
          </div>
          {scoreData.team1_runs > 0 && (
            <div style={{ marginTop: "12px", fontSize: "22px", fontWeight: 800, color: COLORS.text, textAlign: "center" }}>
              {scoreData.team1_runs}/{scoreData.team1_wickets} <span style={{ fontSize: "14px", color: COLORS.textMuted, fontWeight: 600 }}>({scoreData.team1_overs} ov)</span>
            </div>
          )}
        </div>

        {/* TEAM 2 SCORE */}
        <div style={{ ...S.card, borderTop: `3px solid ${COLORS.green}` }}>
          <div style={{ fontSize: "14px", fontWeight: 700, color: COLORS.green, marginBottom: "16px" }}>
            {match.team2} Innings
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px" }}>
            <ScoreInput label="Runs" value={scoreData.team2_runs} onChange={e => setScoreData({ ...scoreData, team2_runs: e.target.value })} />
            <ScoreInput label="Wickets" value={scoreData.team2_wickets} onChange={e => setScoreData({ ...scoreData, team2_wickets: e.target.value })} />
            <ScoreInput label="Overs" type="text" placeholder="e.g. 18.4" value={scoreData.team2_overs} onChange={e => setScoreData({ ...scoreData, team2_overs: e.target.value })} />
          </div>
          {scoreData.team2_runs > 0 && (
            <div style={{ marginTop: "12px", fontSize: "22px", fontWeight: 800, color: COLORS.text, textAlign: "center" }}>
              {scoreData.team2_runs}/{scoreData.team2_wickets} <span style={{ fontSize: "14px", color: COLORS.textMuted, fontWeight: 600 }}>({scoreData.team2_overs} ov)</span>
            </div>
          )}
        </div>

        {/* STATUS */}
        <div style={{ ...S.card }}>
          <div style={S.cardTitle}>Match Status</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
            {[
              { value: "upcoming", label: "Upcoming", color: COLORS.indigo },
              { value: "live", label: "🔴 Live", color: COLORS.green },
              { value: "completed", label: "✅ Completed", color: COLORS.textMuted },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setScoreData({ ...scoreData, status: opt.value })}
                style={{
                  padding: "12px",
                  background: scoreData.status === opt.value ? opt.color + "20" : COLORS.inputBg,
                  border: `1px solid ${scoreData.status === opt.value ? opt.color + "60" : COLORS.cardBorder}`,
                  borderRadius: "10px",
                  color: scoreData.status === opt.value ? opt.color : COLORS.textMuted,
                  fontWeight: 700,
                  fontSize: "12px",
                  cursor: "pointer",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  transition: "all 0.15s",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className={saving ? "" : "admin-btn-hover"}
          style={{
            ...S.btnPrimary,
            width: "100%",
            padding: "16px",
            fontSize: "15px",
            opacity: saving ? 0.7 : 1,
            cursor: saving ? "not-allowed" : "pointer",
            transition: "all 0.15s",
          }}
        >
          {saving ? "Updating..." : "🚀 Update Score Now"}
        </button>
      </form>
    </div>
  );
};

const UpdateScore = () => (
  <ToastProvider>
    <UpdateScoreInner />
  </ToastProvider>
);

export default UpdateScore;
